import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { digitalEmployees } from '../data';
import HeroTeslaRobot from '../components/HeroTeslaRobot';
import TeslaRobot from '../components/TeslaRobot';
import TeslaEmployeeCard from '../components/TeslaEmployeeCard';
import SkillSearch from '../components/SkillSearch';
import CategoryBrowser from '../components/CategoryBrowser';
import IndustryBrowser from '../components/IndustryBrowser';
import HomePageBackground from '../components/HomePageBackground';
import '../hero-section.css';

const NewHomePage: React.FC = () => {
  const [typedText, setTypedText] = useState('');
  const fullText = 'The Future of Work is Here';

  useEffect(() => {
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 100);

    return () => clearInterval(typingInterval);
  }, []);
  
  // Define container style
  const containerStyle: React.CSSProperties = {
    position: 'relative',
  };

  return (
    <div className="home-page" style={containerStyle}>
      <HomePageBackground />
      <div className="hero-section futuristic">
        <div className="hero-content">
          <div className="hero-left">
            <h1>DIGITAL<span className="text-gradient">WORKPLACE</span></h1>
            <div className="typing-container">
              <p className="typing-text">{typedText}<span className="cursor">|</span></p>
            </div>
            <p className="hero-subtitle">Create and interact with AI-powered digital employees tailored to your business needs</p>
            <div className="hero-features-list">
              <div className="hero-feature-item">
                <div className="hero-feature-icon">✓</div>
                <div className="hero-feature-text">Specialized AI employees with unique skills</div>
              </div>
              <div className="hero-feature-item">
                <div className="hero-feature-icon">✓</div>
                <div className="hero-feature-text">Advanced neural chat capabilities</div>
              </div>
              <div className="hero-feature-item">
                <div className="hero-feature-icon">✓</div>
                <div className="hero-feature-text">Seamless integration with your systems</div>
              </div>
            </div>
            <div className="hero-buttons">
              <Link to="/employees" className="cta-button primary-button">
                <span className="button-icon">👾</span> Explore Digital Employees
              </Link>
              <Link to="/demo" className="cta-button secondary-button">
                <span className="button-icon">💻</span> Try Demo
              </Link>
              <Link to="/how-it-works" className="cta-button secondary-button">
                <span className="button-icon">💡</span> How It Works
              </Link>
              <Link to="/pricing" className="cta-button tertiary-button">
                <span className="button-icon">💲</span> View Pricing
              </Link>
            </div>
          </div>
          <div className="hero-right">
            <div className="hero-image-container">
              <HeroTeslaRobot className="main-hero-robot" />
              <div className="hero-image-glow"></div>
              <div className="hero-image-circle"></div>
              <div className="small-robots">
                <TeslaRobot width={80} height={80} className="small-robot robot-1" />
                <TeslaRobot width={60} height={60} className="small-robot robot-2" />
                <TeslaRobot width={70} height={70} className="small-robot robot-3" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="features-section">
        <h2 className="section-title">AI-Powered <span className="text-gradient">Features</span></h2>
        <div className="section-subtitle">Experience the next generation of digital workforce</div>
        <div className="features-grid">
          <div className="feature-card">
            <TeslaRobot width={60} height={60} className="feature-robot" />
            <h3>Skill-Based AI</h3>
            <p>Create digital employees with specialized skills tailored to your specific needs</p>
          </div>
          <div className="feature-card">
            <TeslaRobot width={60} height={60} className="feature-robot" />
            <h3>Interactive Avatars</h3>
            <p>Lifelike digital personas with unique personalities and appearances</p>
          </div>
          <div className="feature-card">
            <TeslaRobot width={60} height={60} className="feature-robot" />
            <h3>Neural Chat</h3>
            <p>Advanced AI-powered conversations that understand context and learn from interactions</p>
          </div>
          <div className="feature-card">
            <TeslaRobot width={60} height={60} className="feature-robot" />
            <h3>Quantum Pricing</h3>
            <p>Revolutionary pricing model that adapts to your usage patterns and requirements</p>
          </div>
        </div>
      </div>

      <CategoryBrowser employees={digitalEmployees} />

      <IndustryBrowser employees={digitalEmployees} />

      <SkillSearch employees={digitalEmployees} />

      <div className="home-api-section">
        <div className="home-api-content">
          <h2 className="section-title">API <span className="text-gradient">Integration</span></h2>
          <div className="section-subtitle">Connect with your custom systems</div>

          <div className="home-api-text">
            <p>
              Our comprehensive API allows you to seamlessly integrate Digital Workplace AI employees
              with your existing systems and workflows. Access and control your digital workforce
              programmatically through our well-documented RESTful API.
            </p>

            <div className="api-features">
              <div className="api-feature">
                <div className="api-feature-icon">🔗</div>
                <div className="api-feature-label">RESTful API</div>
              </div>
              <div className="api-feature">
                <div className="api-feature-icon">💻</div>
                <div className="api-feature-label">Client Libraries</div>
              </div>
              <div className="api-feature">
                <div className="api-feature-icon">🔔</div>
                <div className="api-feature-label">Webhooks</div>
              </div>
              <div className="api-feature">
                <div className="api-feature-icon">🔒</div>
                <div className="api-feature-label">Secure Authentication</div>
              </div>
            </div>
          </div>

          <Link to="/api" className="view-api-button">
            View API Documentation
          </Link>
        </div>
        <div className="home-api-image">
          <div className="code-preview">
            <div className="code-header">
              <div className="code-dots">
                <div className="code-dot"></div>
                <div className="code-dot"></div>
                <div className="code-dot"></div>
              </div>
              <div className="code-title">API Example</div>
            </div>
            <pre className="code-content">
              <code>
{`// Fetch available AI employees
const getEmployees = async () => {
  const response = await fetch(
    'https://api.digitalworkplace.com/v1/employees',
    {
      headers: {
        'X-API-Key': 'YOUR_API_KEY'
      }
    }
  );

  const data = await response.json();
  return data.employees;
};`}
              </code>
            </pre>
          </div>
        </div>
      </div>

      <div className="employees-preview">
        <h2 className="section-title">Featured <span className="text-gradient">AI Assistants</span></h2>
        <div className="section-subtitle">Meet our most advanced digital employees</div>
        <div className="employees-grid">
          {digitalEmployees.slice(0, 2).map(employee => (
            <TeslaEmployeeCard key={employee.id} employee={employee} className="featured-employee" />
          ))}
        </div>
      </div>

      <div className="home-about-section">
        <div className="home-about-content">
          <h2 className="section-title">About <span className="text-gradient">Digital Workplace</span></h2>
          <div className="section-subtitle">Revolutionizing the future of work</div>

          <div className="home-about-text">
            <p>
              Digital Workplace is pioneering the integration of AI technology with business operations,
              creating a platform where human creativity and AI efficiency work together seamlessly.
            </p>
            <div className="founder-info">
              <div className="founder-avatar">DF</div>
              <div className="founder-details">
                <h3>David Feld</h3>
                <p className="founder-title">Creator & CIO</p>
                <p className="founder-description">
                  As the visionary behind Digital Workplace, David leads our innovation strategy and
                  technical direction, shaping the future of AI-powered digital employees.
                </p>
              </div>
            </div>
          </div>

          <Link to="/about" className="learn-more-button">
            Learn More About Us
          </Link>
        </div>
        <div className="home-about-image">
          <TeslaRobot width={200} height={200} className="about-robot" />
        </div>
      </div>

      <div className="home-contact-section">
        <div className="home-contact-content">
          <h2 className="section-title">Get in <span className="text-gradient">Touch</span></h2>
          <div className="section-subtitle">We'd love to hear from you</div>

          <div className="contact-cards">
            <div className="contact-card">
              <div className="contact-card-icon">📞</div>
              <h3>Contact Us</h3>
              <p>Have questions about our digital employees or need assistance?</p>
              <Link to="/contact" className="contact-card-button">
                Contact Support
              </Link>
            </div>

            <div className="contact-card">
              <div className="contact-card-icon">💼</div>
              <h3>Enterprise Solutions</h3>
              <p>Looking for custom AI solutions for your business?</p>
              <Link to="/contact" className="contact-card-button">
                Request a Demo
              </Link>
            </div>

            <div className="contact-card">
              <div className="contact-card-icon">👥</div>
              <h3>Join Our Team</h3>
              <p>Interested in career opportunities at Digital Workplace?</p>
              <Link to="/contact" className="contact-card-button">
                View Careers
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewHomePage;
